/**
 * Parent cookie domain helpers (shared by session + analytics cookies).
 * Kept free of server-only imports so client telemetry can reuse them.
 */

const IPV4_HOSTNAME = /^\d+\.\d+\.\d+\.\d+$/;

/**
 * Parse a hostname or URL into a bare hostname (no port).
 */
export function parseDomain(
	domain: string,
	options?: { preservePort?: boolean },
): string {
	const urlString = domain.includes("://") ? domain : `https://${domain}`;
	const url = new URL(urlString);
	return options?.preservePort ? url.host : url.hostname;
}

/**
 * Parent cookie domain for cross-subdomain sharing (e.g. `.dransay.com`).
 * Host-only for localhost, IPv4, and multi-label `*.dransay.me` preview hosts.
 * Three-label hosts like `shop.dransay.me` share via `.dransay.me`.
 */
export function getParentDomain(domain?: string): string | undefined {
	if (!domain) {
		return undefined;
	}
	let hostname: string;
	try {
		hostname = parseDomain(domain);
	} catch {
		return undefined;
	}
	if (
		hostname === "localhost" ||
		hostname.endsWith(".localhost") ||
		IPV4_HOSTNAME.test(hostname)
	) {
		return undefined;
	}
	const parts = hostname.split(".");
	if (parts.length < 2) {
		return undefined;
	}
	// Multi-label *.dransay.me (PR previews / deep staging) stay host-only.
	if (
		parts.length >= 4 &&
		parts[parts.length - 1] === "me" &&
		parts[parts.length - 2] === "dransay"
	) {
		return undefined;
	}
	return `.${parts.slice(-2).join(".")}`;
}
